import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, Link } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import SkipLink from './components/SkipLink';
import { LanguageProvider } from './contexts/LanguageContext';
import { ContentLanguageProvider } from './contexts/ContentLanguageContext';
import { StepProvider } from './contexts/StepContext';
import { AuthProvider } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { AccessibilityProvider } from './components/ui';
import AccessibilityEnhancer from './components/AccessibilityEnhancer';
import { useAuth } from './contexts/AuthContext';
import { supabase } from './lib/supabaseClient';
import { initMonitoring } from './utils/monitoring';
import { trackCustomError } from './utils/errorTracking';
import { useAnalytics } from './hooks/useAnalytics';
import DashboardLayout from './layouts/DashboardLayout';
import { isRouteEnabled } from './config/mvpConfig';
import {
  LazyDashboard,
  LazyLandingPage,
  LazyJourneyPage,
  LazyAuthPage,
  LazyCourseDetail,
  LazyJourneyPath,
  LazyBiteLesson,
  LazyTopicPage,
  LazyLearnLesson,
  LazyPracticeHub,
  LazyPracticeSession,
  LazyExam,
  LazyLessonDetail,
  LazyQuickCoursePage,
  LazyProfileSetupPage,
  LazyLibrary,
  LazyToolkitPage,
  LazyAnalyticsPage,
  LazyAchievementsPage,
  LazyReflectionsPage,
  LazyChatPage,
  LazySubscriptionPage,
  LazyStripeTestPage,
  LazyAdminPage,
  LazyContentManagementPage,
  LazyUserTypeTestPage,
  LazyIntentDrivenDemoPage,
  LazyBankingCredit,
  LazyBudgetingSaving,
  LazyTaxSelfEmployment,
  LazyHousingUtilities,
  LazyInsuranceProtection,
  LazyEducationRetirement,
  LazyInitialOnboardingChecklist,
  LazyInitialDocumentSetup,
  LazySafetyEmergency,
  LazyOnboarding,
  LazyStarterLesson,
  LazyFirstLesson,
  LazyWidgetHint,
  LazyPassOffer,
  LazyAccountPrompt,
  LazyWelcomeBack
} from './pages/lazy';
import CourseLessonRoute, { CoursePathRoute, PublicAccountRoute } from './features/onboarding/CourseLessonRoute';
import ReviewDeckPage from './features/journey-practice/ReviewDeckPage';
import LessonFinishPage from './features/journey-practice/LessonFinish';
import ExplorePage from './features/explore/ExplorePage';
import MePage from './features/me/MePage';
import SettingsPage from './features/me/SettingsPage';
import ProReminder from './features/onboarding/ProReminder';
import ProPaywall from './features/journey-practice/ProPaywall';
import { PrivacyPage, TermsPage } from './pages/LegalPage';
import LazyGuidesPage from './pages/GuidesPage';
import LazyMilestonesPage from './pages/MilestonesPage';
import LazyNextStepsPage from './pages/NextStepsPage';
import LazyMyToolsResourcesPage from './pages/MyToolsResourcesPage';
import LazyConnectionsPage from './pages/ConnectionsPage';
import LazyUpcomingPage from './pages/UpcomingPage';
import LazyProfileSettingsPage from './pages/ProfileSettingsPage';
import LazyLearnPage from './pages/LearnPage';
import LazyNextPage from './pages/NextPage';
import LazyCommunityPage from './pages/CommunityPage';
import LazyMorePage from './pages/MorePage';
import Help from './pages/Help';
// Onboarding steps are now lazy-loaded from ./pages/lazy
import MiniAngel from './components/MiniAngel';
import { initAnalytics } from './utils/analytics';
import { initMobileOptimizations } from './utils/mobileOptimization';
import { initBundleOptimizations } from './utils/bundleOptimization';

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading, profileComplete } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace state={{ from: location }} />;
  }

  // If user is authenticated but profile is not complete, redirect to profile setup
  // unless they're already on the profile setup page
  if (!profileComplete && location.pathname !== '/profile-setup') {
    return <Navigate to="/profile-setup" replace />;
  }

  return <>{children}</>;
};

const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user) {
        setIsAdmin(false);
        setCheckingAdmin(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', user.id)
          .maybeSingle();

        if (error) throw error;
        setIsAdmin(data?.role === 'admin');
      } catch (error) {
        console.error('Error checking admin status:', error);
        setIsAdmin(false);
      } finally {
        setCheckingAdmin(false);
      }
    };

    if (!loading) {
      checkAdminStatus();
    }
  }, [user, loading]);

  if (loading || checkingAdmin) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user || !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

const HelpRoute: React.FC = () => {
  const { user, loading, profileComplete, profileCheckSettled } = useAuth();

  // Help is static content. Do not wait on the auth/profile fetch: that
  // promise can hang (Supabase auth lock, missing config, stalled network)
  // and previously left signed-out visitors on an infinite spinner or the
  // lazy "Loading help..." fallback. A timed-out profile check stays
  // unsettled so we keep this page instead of redirecting away.
  const helpPage = <Help />;

  if (!loading && user && profileComplete) {
    return <DashboardLayout>{helpPage}</DashboardLayout>;
  }

  if (!loading && user && profileCheckSettled && !profileComplete) {
    return <Navigate to="/profile-setup" replace />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Link to="/" className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium">
          Back to Home
        </Link>
        <div className="mt-6">
          {helpPage}
        </div>
      </div>
    </div>
  );
};

const AppContent: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const { trackEvent } = useAnalytics();
  
  // Initialize monitoring, mobile optimizations, and bundle optimizations
  useEffect(() => {
    initMonitoring();
    initMobileOptimizations();
    initBundleOptimizations();
  }, []);

  // Register service worker for offline support
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw-offline.js')
        .then((registration) => {
          console.log('SW registered: ', registration);
        })
        .catch((registrationError) => {
          console.log('SW registration failed: ', registrationError);
        });
    }
  }, []);
  
  // Don't show MiniAngel on these pages
  const hideFloatingChatOn = [
    '/auth',
    '/profile-setup',
    '/chat',
    '/admin',
    '/stripe-test',
    '/pro',
    '/lesson-done',
  ];
  
  // Check if we're on a page where we should hide the floating chat
  const shouldHideFloatingChat = 
    !user || 
    hideFloatingChatOn.some(path => location.pathname.startsWith(path)) ||
    location.pathname.includes('/bites/') ||
    location.pathname.includes('/learn') ||
    window.innerWidth < 768; // Hide on mobile screens (where bottom nav is visible)

  // Initialize analytics
  useEffect(() => {
    if (user) {
      initAnalytics();
    }
  }, [user]);

  // Track route changes
  useEffect(() => {
    if (user) {
      trackEvent('page_view', {
        page_path: location.pathname,
        page_title: document.title
      });
    }
  }, [location.pathname, user]);

  // Global error handler for unhandled promise rejections
  useEffect(() => {
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error('Unhandled promise rejection:', event.reason);
      trackCustomError('Unhandled promise rejection', {
        reason: event.reason?.message || 'Unknown error',
        stack: event.reason?.stack,
        pathname: location.pathname
      });
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    return () => window.removeEventListener('unhandledrejection', handleUnhandledRejection);
  }, [location.pathname]);

  return (
    <>
      <ProReminder />
      <Routes>
        {/* Public routes */}
        <Route path="/" element={user ? <Navigate to="/dashboard" replace /> : <LazyLandingPage />} />
        <Route path="/start" element={user ? <Navigate to="/dashboard" replace /> : <LazyOnboarding />} />
        <Route path="/home" element={user ? <Navigate to="/dashboard" replace /> : <LazyDashboard />} />
        <Route path="/review" element={<ReviewDeckPage />} />
        <Route path="/lesson-done" element={<LessonFinishPage />} />
        <Route path="/explore" element={<PublicAccountRoute><ExplorePage /></PublicAccountRoute>} />
        <Route path="/me" element={<PublicAccountRoute><MePage /></PublicAccountRoute>} />
        <Route path="/settings" element={<PublicAccountRoute><SettingsPage /></PublicAccountRoute>} />
        <Route path="/pro" element={<ProPaywall />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/start/lesson" element={<LazyStarterLesson />} />
        <Route path="/start/drill" element={<LazyFirstLesson />} />
        <Route path="/start/widget" element={<LazyWidgetHint />} />
        <Route path="/start/pass" element={<LazyPassOffer />} />
        <Route path="/start/account" element={<LazyAccountPrompt />} />
        <Route path="/welcome-back" element={<LazyWelcomeBack />} />
        <Route path="/auth" element={user ? <Navigate to="/dashboard" replace /> : <LazyAuthPage />} />
        
        {/* Profile setup route */}
        <Route path="/profile-setup" element={
          user ? <LazyProfileSetupPage /> : <Navigate to="/auth" replace />
        } />
        
        {/* Admin routes */}
        <Route path="/admin/*" element={
          <AdminRoute>
            <LazyAdminPage />
          </AdminRoute>
        } />
        
        {/* Dedicated chat page */}
        <Route path="/chat" element={
          <ProtectedRoute>
            <DashboardLayout>
              <LazyChatPage />
            </DashboardLayout>
          </ProtectedRoute>
        } />
        
        {/* Subscription page */}
        <Route path="/subscription" element={
          <ProtectedRoute>
            <LazySubscriptionPage />
          </ProtectedRoute>
        } />

        {/* Stripe test page */}
        <Route path="/stripe-test" element={
          <ProtectedRoute>
            <LazyStripeTestPage />
          </ProtectedRoute>
        } />
        
        {/* User Type Testing page - Development only */}
        {process.env.NODE_ENV === 'development' && (
          <Route path="/user-type-test" element={
            <ProtectedRoute>
              <DashboardLayout>
                <LazyUserTypeTestPage />
              </DashboardLayout>
            </ProtectedRoute>
          } />
        )}
        
        {/* Protected routes */}
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <DashboardLayout>
              <LazyDashboard />
            </DashboardLayout>
          </ProtectedRoute>
        } />
        
        
        <Route path="/guides" element={
          <ProtectedRoute>
            <DashboardLayout>
              <LazyGuidesPage />
            </DashboardLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/journey" element={
          <ProtectedRoute>
            <DashboardLayout>
              <LazyJourneyPage />
            </DashboardLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/journey/:slug" element={
          <ProtectedRoute>
            <DashboardLayout>
              <LazyGuidesPage />
            </DashboardLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/milestones" element={
          <ProtectedRoute>
            <DashboardLayout>
              <LazyMilestonesPage />
            </DashboardLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/next-steps" element={
          <ProtectedRoute>
            <DashboardLayout>
              <LazyNextStepsPage />
            </DashboardLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/connections" element={
          <ProtectedRoute>
            <DashboardLayout>
              <LazyConnectionsPage />
            </DashboardLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/upcoming" element={
          <ProtectedRoute>
            <DashboardLayout>
              <LazyUpcomingPage />
            </DashboardLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/profile-settings" element={
          <ProtectedRoute>
            <DashboardLayout>
              <LazyProfileSettingsPage />
            </DashboardLayout>
          </ProtectedRoute>
        } />
        
        {/* Mobile-First Navigation Routes */}
        <Route path="/learn" element={
          <ProtectedRoute>
            <DashboardLayout>
              <LazyLearnPage />
            </DashboardLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/next" element={
          <ProtectedRoute>
            <DashboardLayout>
              <LazyNextPage />
            </DashboardLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/community" element={
          <ProtectedRoute>
            <DashboardLayout>
              <LazyCommunityPage />
            </DashboardLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/more" element={
          <ProtectedRoute>
            <DashboardLayout>
              <LazyMorePage />
            </DashboardLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/quick-course/:courseId" element={
          <ProtectedRoute>
            <DashboardLayout>
              <LazyQuickCoursePage />
            </DashboardLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/toolkit" element={
          <ProtectedRoute>
            <DashboardLayout>
              <LazyToolkitPage />
            </DashboardLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/courses/:slug" element={
          <CoursePathRoute>
            <LazyJourneyPath />
          </CoursePathRoute>
        } />

        <Route path="/courses/:slug/notes" element={
          <ProtectedRoute>
            <DashboardLayout>
              <LazyCourseDetail />
            </DashboardLayout>
          </ProtectedRoute>
        } />

        <Route path="/courses/:slug/topics/:lessonSlug/learn" element={
          <CourseLessonRoute kind="lesson">
            <LazyLearnLesson />
          </CourseLessonRoute>
        } />

        <Route path="/courses/:slug/topics/:lessonSlug" element={
          <CourseLessonRoute kind="lesson">
            <LazyTopicPage />
          </CourseLessonRoute>
        } />

        <Route path="/courses/:slug/bites/:lessonSlug" element={
          <CourseLessonRoute kind="lesson">
            <LazyBiteLesson />
          </CourseLessonRoute>
        } />

        <Route path="/courses/:slug/practice" element={
          <CoursePathRoute>
            <LazyPracticeHub />
          </CoursePathRoute>
        } />

        <Route path="/courses/:slug/practice/:mode" element={
          <CourseLessonRoute kind="practice">
            <LazyPracticeSession />
          </CourseLessonRoute>
        } />

        <Route path="/courses/:slug/exam" element={
          <CoursePathRoute>
            <LazyExam />
          </CoursePathRoute>
        } />
        
        <Route path="/courses/:journeySlug/lessons/:lessonSlug" element={
          <ProtectedRoute>
            <DashboardLayout>
              <LazyLessonDetail />
            </DashboardLayout>
          </ProtectedRoute>
        } />
        
        
        <Route path="/library" element={
          <ProtectedRoute>
            <DashboardLayout>
              <LazyMyToolsResourcesPage />
            </DashboardLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/help" element={<HelpRoute />} />

        <Route path="/intent-demo" element={
          <ProtectedRoute>
            <DashboardLayout>
              <LazyIntentDrivenDemoPage />
            </DashboardLayout>
          </ProtectedRoute>
        } />
        
        
        <Route path="/reflections" element={
          <ProtectedRoute>
            <DashboardLayout>
              <LazyReflectionsPage />
            </DashboardLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/achievements" element={
          <ProtectedRoute>
            <DashboardLayout>
              <LazyAchievementsPage />
            </DashboardLayout>
          </ProtectedRoute>
        } />
        {/* Analytics route - disabled for MVP if not enabled in config */}
        {isRouteEnabled('/analytics') && (
          <Route path="/analytics" element={
            <ProtectedRoute>
              <DashboardLayout>
                <LazyAnalyticsPage />
              </DashboardLayout>
            </ProtectedRoute>
          } />
        )}
        

        <Route path="/steps/welcome-setup" element={
          <ProtectedRoute>
            <DashboardLayout>
              <LazyInitialOnboardingChecklist />
            </DashboardLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/steps/initial-documents" element={
          <ProtectedRoute>
            <DashboardLayout>
              <LazyInitialDocumentSetup />
            </DashboardLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/steps/safety-emergency" element={
          <ProtectedRoute>
            <DashboardLayout>
              <LazySafetyEmergency />
            </DashboardLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/steps/banking-credit" element={
          <ProtectedRoute>
            <DashboardLayout>
              <LazyBankingCredit />
            </DashboardLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/steps/budgeting-saving" element={
          <ProtectedRoute>
            <DashboardLayout>
              <LazyBudgetingSaving />
            </DashboardLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/steps/tax-employment" element={
          <ProtectedRoute>
            <DashboardLayout>
              <LazyTaxSelfEmployment />
            </DashboardLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/steps/housing-utilities" element={
          <ProtectedRoute>
            <DashboardLayout>
              <LazyHousingUtilities />
            </DashboardLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/steps/insurance-protection" element={
          <ProtectedRoute>
            <DashboardLayout>
              <LazyInsuranceProtection />
            </DashboardLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/steps/education-retirement" element={
          <ProtectedRoute>
            <DashboardLayout>
              <LazyEducationRetirement />
            </DashboardLayout>
          </ProtectedRoute>
        } />
      </Routes>
      {!shouldHideFloatingChat && <MiniAngel />}
    </>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <AccessibilityProvider>
        <BrowserRouter>
          <SkipLink />
          <AuthProvider>
            <LanguageProvider>
              <ContentLanguageProvider>
              <StepProvider>
                <ToastProvider>
                  <AccessibilityEnhancer>
                    <AppContent />
                  </AccessibilityEnhancer>
                </ToastProvider>
              </StepProvider>
              </ContentLanguageProvider>
            </LanguageProvider>
          </AuthProvider>
        </BrowserRouter>
      </AccessibilityProvider>
    </ErrorBoundary>
  );
}

export default App;